import { useEffect, useState } from "react";
import TabsSelection from "../shared-components/TabsSelection";
import {
  ACTOR_TIMER_STATUS_UI_ENUM,
  ReportSummaryItemApi,
} from "../api/Reports";
import WebContentReview from "./WebContentReview";
import { ArticleActions, ItemTypes } from "../api/Items";
import Button from "../shared-components/Button";
import { useApplicationToast } from "./ApplicationToastProvider";
import {
  useArticleManualEditDone,
  useArticleManualRateDone,
} from "../api/WorkflowApi";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import {
  useFetchItemsForReview,
  useGetWebContentScrapeRecord,
} from "../api/ItemsApi";
import { ExclamationCircleIcon as ExclamationIcon } from "@heroicons/react/24/outline";
import {
  useReportDetail,
  useReportTimer,
} from "../reports/ReportDetailProvider";
import { useWorkflowStepActorTimer } from "../reports/useWorkflowStepActorTimer";
import { WORKFLOW_STEP_STATUS_ENUM } from "../api/Workflows";
import { WebContentErrors, useWebContentActions } from "./useWebContentActions";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { WEBCONTENT_TABS, WebcontentTabEnum } from "./helper";
import AffilitionList from "../affiliation/AffilitionList";
import { ITEM_SOURCE_AFFILIATION } from "../affiliation/helpers";

const emptyItemResponse = {
  items: [],
  total: 0,
  page: 1,
  size: 10,
};

const defaultPageSizeReviewOptions = [
  { id: "10", label: "10" },
  { id: "25", label: "25" },
  { id: "50", label: "50" },
];

type WebcontentProps = {
  isReadOnly?: boolean;
  report?: ReportSummaryItemApi;
  handleExit: () => void;
  isAmendment: boolean;
  dateFilter: { fromDate: string; toDate: string };
};

const WebContent = ({
  isReadOnly,
  report,
  handleExit,
  isAmendment,
  dateFilter, // TODO: not filtering by dates?
}: WebcontentProps) => {
  if (!report) {
    return <span data-testid="empty-reports">Report Cannot be loaded.</span>;
  }
  const initialTab = isAmendment
    ? WebcontentTabEnum.ADDED
    : WebcontentTabEnum.REVIEW;

  return (
    <WebcontentTabSelection
      handleExit={handleExit}
      isReadOnly={isReadOnly}
      report={report}
      isAmendment={isAmendment}
      initialTab={initialTab}
    />
  );
};

function webcontentErrorToString(webcontentError: WebContentErrors) {
  let errors = [];
  for (let key of Object.keys(webcontentError)) {
    if (webcontentError[key as ArticleActions]) {
      errors.push(key.toLocaleLowerCase());
    }
  }
  return errors.join(", ");
}

function WebcontentTabSelection({
  report,
  initialTab,
  isReadOnly,
  handleExit,
  isAmendment,
}: {
  report: ReportSummaryItemApi;
  initialTab?: WebcontentTabEnum;
  isReadOnly?: boolean;
  isAmendment: boolean;
  handleExit: () => void;
}) {
  const { showLargeToast } = useApplicationToast();
  const [showDoneBtn, setShowDoneBtn] = useState<boolean>(false);
  const { resetTimer } = useReportTimer();
  const webContentActorStep = isAmendment
    ? WORKFLOW_STEP_STATUS_ENUM.ArticlesManualEdit
    : WORKFLOW_STEP_STATUS_ENUM.WebContentReview;
  const postWebContentActorTimer = useWorkflowStepActorTimer(
    report.workflow_run_uuid,
    webContentActorStep
  );
  const [didScrapeFail, setDidScrapeFail] = useState(false);

  const { mutate: getWebContentScrapeRecord, data: webContentScrapeRecord } =
    useGetWebContentScrapeRecord();

  const {
    articlesHeap,
    refreshArticlesByAction,
    appendItemToHeap,
    isLoading: isLoadingArticles,
    errorLoadingArticles,
    reviewPageSize,
    setReviewPageSize,
  } = useWebContentActions(report.report_uuid);

  const {
    blueprint,
    isLoadingBlueprint,
    isSuccessLoadingBlueprintAnnotated,
    errorBlueprint,
  } = useReportDetail();

  const {
    isLoading: isLoadingWebContentProfile,
    mutate: loadWebContentProfile,
    error: errorWebContentProfile,
    data: webContentProfile,
    isSuccess: isSuccessWebContentProfile,
  } = useFetchItemsForReview();

  useEffect(() => {
    if (report.report_uuid && !webContentScrapeRecord) {
      getWebContentScrapeRecord(report.report_uuid);
    }
    if (blueprint) {
      const hasWebContent = !!blueprint.item_sources.find(
        (source) => source.name === "Web Content"
      );
      const scrapeFinished =
        !!webContentScrapeRecord &&
        webContentScrapeRecord.filter((scrape: any) => !!scrape?.item_count)
          .length /
          webContentScrapeRecord.length >=
          0.8;
      const scrapeFailed = hasWebContent && !scrapeFinished;
      setDidScrapeFail(scrapeFailed);
    }
  }, [report, getWebContentScrapeRecord, webContentScrapeRecord, blueprint]);

  useEffect(() => {
    if (report?.blueprint_uuid) {
      loadWebContentProfile({
        report_uuid: report?.report_uuid,
        item_type_name: [ItemTypes.WEB_CONTENT_PROFILE],
        page_num: 1,
        page_size: 1,
      });
    }
  }, [report, loadWebContentProfile]);

  const navigate = useNavigate();
  const {
    mutate: articleRateDone,
    isLoading: isLoadingComplete,
    error: errorComplete,
    isSuccess: isSuccessComplete,
    reset: resetComplete,
  } = useArticleManualRateDone();

  const {
    mutate: articleEditDone,
    isLoading: isLoadingEditComplete,
    error: errorEditComplete,
    isSuccess: isSuccessEditComplete,
    reset: resetEditComplete,
  } = useArticleManualEditDone();

  function completeWebcontent() {
    postWebContentActorTimer(ACTOR_TIMER_STATUS_UI_ENUM.CLOSED);
    if (isAmendment) {
      articleEditDone({
        reportUUID: report.report_uuid,
        workflowRunUUID: report.workflow_run_uuid,
      });
    } else {
      articleRateDone({
        reportUUID: report.report_uuid,
        workflowRunUUID: report.workflow_run_uuid,
      });
    }
  }

  useEffect(() => {
    if (isSuccessComplete || isSuccessEditComplete) {
      if (isAmendment) {
        navigate("/reports/amendment", { state: { stepDone: true } });
      } else {
        navigate("/reports/next_task", { state: { stepDone: true } });
      }
    }
  }, [navigate, isAmendment, isSuccessComplete, isSuccessEditComplete]);

  useEffect(() => {
    if (errorComplete || errorEditComplete) {
      showLargeToast?.({
        title: "Error!",
        message:
          "Couldn't advance the article, please try again or contact the administrator.",
        icon: (
          <div className="bg-red-100 text-red-600 rounded-full p-2">
            <ExclamationCircleIcon className="w-6" />
          </div>
        ),
      });
      if (isAmendment) {
        resetEditComplete();
      } else {
        resetComplete();
      }
      resetTimer?.();
    }
  }, [
    errorComplete,
    errorEditComplete,
    isAmendment,
    showLargeToast,
    resetEditComplete,
    resetComplete,
    resetTimer,
  ]);

  const [currentTab, setCurrentTab] = useState({
    tab: initialTab ?? WebcontentTabEnum.REVIEW,
    time: new Date(),
  });

  function changeTab(newTab: WebcontentTabEnum) {
    setCurrentTab({ tab: newTab, time: new Date() });
    resetTimer?.();
  }

  const tab_list = isAmendment
    ? [WEBCONTENT_TABS.added, WEBCONTENT_TABS.removed]
    : [WEBCONTENT_TABS.review, WEBCONTENT_TABS.added, WEBCONTENT_TABS.removed];

  const hasAffiliationType = blueprint?.item_sources?.find(
    (source) => source.name === ITEM_SOURCE_AFFILIATION
  );

  if (hasAffiliationType) {
    tab_list.push(WEBCONTENT_TABS.affiliation);
  }

  const activateDoneBtn = (value: boolean) => {
    setShowDoneBtn(value);
  };

  const profileUUID: string =
    webContentProfile?.items?.[0]?.item?.external_id ?? "";

  useEffect(() => {
    if (isSuccessLoadingBlueprintAnnotated && isSuccessWebContentProfile) {
      resetTimer?.();
    }
  }, [
    isSuccessLoadingBlueprintAnnotated,
    isSuccessWebContentProfile,
    resetTimer,
  ]);

  function handleNextPage(
    articleAction: ArticleActions,
    page: number,
    keep?: boolean,
    newPageSize?: number
  ) {
    if (newPageSize) {
      setReviewPageSize(newPageSize);
    }
    refreshArticlesByAction(articleAction, page, keep, newPageSize);
    resetTimer?.();
  }

  return (
    <>
      <div className="flex justify-end items-center gap-3">
        {(isLoadingBlueprint || isLoadingWebContentProfile) && (
          <LoadingSpinner />
        )}
        {errorBlueprint ||
          (errorWebContentProfile && (
            <span
              className="text-red-500"
              title="Error Loading profile or behaviors"
            >
              <ExclamationIcon className="w-4" />
            </span>
          ))}
        <span
          className="text-xs font-medium text-orange-500 cursor-pointer ml-auto"
          onClick={handleExit}
        >
          {isAmendment ? "Back to List" : "Exit Web Content"}
        </span>
        {(isLoadingComplete && isLoadingEditComplete) || isLoadingArticles ? (
          <LoadingSpinner />
        ) : (
          !isReadOnly && (
            <Button
              disabled={!showDoneBtn && !isAmendment}
              text="Complete Web Content Review"
              onClick={completeWebcontent}
              className=""
            />
          )
        )}
      </div>
      <TabsSelection
        tabs={tab_list}
        initial={currentTab.tab}
        onChange={(tabChange) => {
          changeTab(tabChange.identifier as WebcontentTabEnum);
        }}
      ></TabsSelection>
      {(errorLoadingArticles.REVIEW ||
        errorLoadingArticles.ADDED ||
        errorLoadingArticles.REMOVED) && (
        <div className="py-4">
          <ErrorMessageContainer
            message={`Error loading ${webcontentErrorToString(
              errorLoadingArticles
            )} articles`}
          />
        </div>
      )}
      {currentTab.tab === WebcontentTabEnum.REVIEW && !isAmendment && (
        <WebContentReview
          report={report}
          isReadOnly={isReadOnly}
          handleExit={handleExit}
          articleAction={ArticleActions.REVIEW}
          behaviors={blueprint?.behaviors}
          activateDoneBtn={activateDoneBtn}
          articles={articlesHeap.REVIEW ?? emptyItemResponse}
          handleNextPage={handleNextPage}
          handleAppend={appendItemToHeap}
          isLoadingArticles={isLoadingArticles}
          pageSizeReviewOptions={defaultPageSizeReviewOptions}
          pageSizeSelection={defaultPageSizeReviewOptions.find(
            (p) => p.id === reviewPageSize.toString()
          )}
          isAmendment={isAmendment}
          webContentParent={webContentProfile?.items?.[0]?.item}
          didScrapeFail={didScrapeFail}
          profileUUID={profileUUID}
        ></WebContentReview>
      )}
      {currentTab.tab === WebcontentTabEnum.ADDED && (
        <WebContentReview
          report={report}
          isReadOnly={isReadOnly}
          handleExit={handleExit}
          articleAction={ArticleActions.ADDED}
          time={currentTab.time}
          behaviors={blueprint?.behaviors}
          articles={articlesHeap.ADDED ?? emptyItemResponse}
          handleNextPage={handleNextPage}
          handleAppend={appendItemToHeap}
          isLoadingArticles={isLoadingArticles}
          isAmendment={isAmendment}
          webContentParent={webContentProfile?.items?.[0]?.item}
          didScrapeFail={didScrapeFail}
          profileUUID={profileUUID}
        ></WebContentReview>
      )}
      {currentTab.tab === WebcontentTabEnum.REMOVED && (
        <WebContentReview
          report={report}
          isReadOnly={isReadOnly}
          handleExit={handleExit}
          articleAction={ArticleActions.REMOVED}
          articles={articlesHeap.REMOVED ?? emptyItemResponse}
          behaviors={[]}
          handleNextPage={handleNextPage}
          handleAppend={appendItemToHeap}
          isLoadingArticles={isLoadingArticles}
          isAmendment={isAmendment}
          webContentParent={webContentProfile?.items?.[0]?.item}
          didScrapeFail={didScrapeFail}
          profileUUID={profileUUID}
        ></WebContentReview>
      )}
      {currentTab.tab === WebcontentTabEnum.AFFILIATION && (
        <AffilitionList
          report={report}
          isReadOnly={isReadOnly ?? false}
          handleExit={handleExit}
        ></AffilitionList>
      )}
    </>
  );
}

export default WebContent;
